/**
 * CFMoto unit fees — Procore Power Equipment
 *
 * Keeps Incoming Freight, Title Fee and Doc Fee lines in the cart in sync
 * with any CFMoto unit lines, at any entry point (product page, quick add,
 * cart page, mini cart). The Shopify validation function is the server-side
 * backstop; this script is what keeps the customer from ever seeing an error.
 *
 * How it works
 *  1. Wraps window.fetch. After any /cart/add, /cart/change or /cart/update
 *     request completes, reconcile() runs before the theme sees the response,
 *     so the theme's own cart re-render already includes the fee lines.
 *  2. reconcile() reads /cart.js, looks up each line's product tags
 *     (/products/{handle}.js, cached in sessionStorage), works out the fee
 *     quantities that should exist, and sets them with /cart/update.js.
 *  3. On every page load reconcile() runs once, which covers full-page cart
 *     changes and anything that happened in another tab.
 *  4. Fee lines get a CSS class so the theme hides their quantity/remove
 *     controls (see cfmoto-fees.css in the same asset folder).
 *
 * Config: variant IDs live in CONFIG below. Prices live in Shopify admin on
 * the fee products themselves — nothing here needs to change when a price does.
 */
(function () {
  'use strict';
 
  var CONFIG = {
    unitTag: 'cfmoto-unit',
    // product tag -> Incoming Freight variant ID
    freightVariants: {
      'freight:atv': 53152339296576,
      'freight:utv': 53152339329344,
      'freight:utv-crew': 53152339362112
    },
    titleVariant: 53152255181120,
    docVariant: 53152295092544,
    // handles of the fee products, used to recognise fee lines in the cart
    feeHandles: ['incoming-freight', 'title-fee', 'doc-fee'],
    debug: false
  };
 
  var INTERNAL_HEADER = 'X-CFMoto-Fees';
  var CART_URL = /\/cart\/(add|change|update)(\.js)?(\?|$)/;
  var tagCache = {};
  var reconciling = null;
 
  function log() {
    if (CONFIG.debug && window.console) console.log.apply(console, ['[cfmoto-fees]'].concat([].slice.call(arguments)));
  }
 
  function allFeeVariants() {
    var ids = [CONFIG.titleVariant, CONFIG.docVariant];
    for (var k in CONFIG.freightVariants) ids.push(CONFIG.freightVariants[k]);
    return ids;
  }
 
  function isFeeLine(item) {
    return CONFIG.feeHandles.indexOf(item.handle) !== -1 || allFeeVariants().indexOf(item.variant_id) !== -1;
  }
 
  // ---- tag lookup ----------------------------------------------------------
 
  function loadCache() {
    try {
      var raw = sessionStorage.getItem('cfmoto-fees-tags');
      if (raw) tagCache = JSON.parse(raw) || {};
    } catch (e) { tagCache = {}; }
  }
 
  function saveCache() {
    try { sessionStorage.setItem('cfmoto-fees-tags', JSON.stringify(tagCache)); } catch (e) { /* ignore */ }
  }
 
  function tagsFor(handle) {
    if (tagCache[handle]) return Promise.resolve(tagCache[handle]);
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : { tags: [] }; })
      .then(function (p) {
        var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
        tagCache[handle] = tags;
        saveCache();
        return tags;
      })
      .catch(function () { return []; });
  }
 
  // ---- core ----------------------------------------------------------------
 
  function internalFetch(url, opts) {
    opts = opts || {};
    opts.credentials = 'same-origin';
    opts.headers = Object.assign({}, opts.headers || {}, { 'Content-Type': 'application/json' });
    opts.headers[INTERNAL_HEADER] = '1';
    return window.fetch(url, opts);
  }
 
  function getCart() {
    return internalFetch('/cart.js', { method: 'GET' }).then(function (r) { return r.json(); });
  }
 
  /** Work out required fee quantities from the unit lines in the cart. */
  function requiredFees(cart) {
    var lines = cart.items.filter(function (i) { return !isFeeLine(i); });
    return Promise.all(lines.map(function (line) {
      return tagsFor(line.handle).then(function (tags) { return { line: line, tags: tags }; });
    })).then(function (rows) {
      var need = {};
      allFeeVariants().forEach(function (id) { need[id] = 0; });
      rows.forEach(function (row) {
        if (row.tags.indexOf(CONFIG.unitTag) === -1) return;
        var qty = row.line.quantity;
        need[CONFIG.titleVariant] += qty;
        need[CONFIG.docVariant] += qty;
        for (var tag in CONFIG.freightVariants) {
          if (row.tags.indexOf(tag) !== -1) { need[CONFIG.freightVariants[tag]] += qty; break; }
        }
      });
      return need;
    });
  }
 
  function currentFees(cart) {
    var have = {};
    allFeeVariants().forEach(function (id) { have[id] = 0; });
    cart.items.forEach(function (i) {
      if (have.hasOwnProperty(i.variant_id)) have[i.variant_id] += i.quantity;
    });
    return have;
  }
 
  /**
   * Make the cart's fee lines match the unit lines. Resolves true when
   * something was changed, false when the cart was already correct.
   */
  function reconcile() {
    if (reconciling) return reconciling;
    reconciling = getCart().then(function (cart) {
      return requiredFees(cart).then(function (need) {
        var have = currentFees(cart);
        var updates = {};
        var changed = false;
        for (var id in need) {
          if (need[id] !== have[id]) { updates[id] = need[id]; changed = true; }
        }
        if (!changed) { log('cart already in sync'); return false; }
        log('updating fee lines', updates);
        return internalFetch('/cart/update.js', { method: 'POST', body: JSON.stringify({ updates: updates }) })
          .then(function (r) { return r.json(); })
          .then(function (after) {
            // /cart/update.js can't add a variant that isn't already in the cart
            // on every storefront config, so add any that are still missing.
            var haveNow = currentFees(after);
            var toAdd = [];
            for (var vid in need) {
              if (need[vid] > 0 && haveNow[vid] === 0) toAdd.push({ id: Number(vid), quantity: need[vid], properties: { _fee: 'cfmoto' } });
            }
            if (!toAdd.length) return true;
            return internalFetch('/cart/add.js', { method: 'POST', body: JSON.stringify({ items: toAdd }) })
              .then(function () { return true; });
          });
      });
    }).catch(function (e) {
      log('reconcile failed', e);
      return false;
    }).then(function (res) {
      reconciling = null;
      return res;
    });
    return reconciling;
  }
 
  // ---- fetch interception --------------------------------------------------
 
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var headers = (init && init.headers) || (input && input.headers) || {};
    var internal = headers && (headers[INTERNAL_HEADER] || (typeof headers.get === 'function' && headers.get(INTERNAL_HEADER)));
    var p = nativeFetch.apply(window, arguments);
    if (internal || !CART_URL.test(url)) return p;
    // Let the theme's request finish, sync fees, then hand the response back.
    return p.then(function (response) {
      return reconcile().then(function () { return response; });
    });
  };
 
  // Older theme code paths use XMLHttpRequest; cover those too.
  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_URL.test(String(url))) {
      this.addEventListener('load', function () { reconcile().then(refreshTheme); });
    }
    return nativeOpen.apply(this, arguments);
  };
 
  // ---- theme hooks ---------------------------------------------------------
 
  /** Ask Warehouse to re-render the mini cart / cart section. */
  function refreshTheme() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { scrollToTop: false } }));
  }
 
  /** Mark fee line items so CSS can hide their quantity/remove controls. */
  function markFeeLines() {
    var sel = CONFIG.feeHandles.map(function (h) { return 'a[href*="/products/' + h + '"]'; }).join(',');
    document.querySelectorAll(sel).forEach(function (a) {
      var row = a.closest('.line-item, .mini-cart__line-item, [data-line-item], tr, li');
      if (!row) return;
      row.classList.add('cfmoto-fee-line');
      // Expose the (locked) quantity so CSS can print it in place of the stepper.
      row.querySelectorAll('.line-item__quantity, .mini-cart__quantity').forEach(function (wrap) {
        var input = wrap.querySelector('.quantity-selector__value');
        if (input) wrap.setAttribute('data-cfmoto-qty', input.value);
      });
    });
  }
 
  function init() {
    loadCache();
    markFeeLines();
    new MutationObserver(markFeeLines).observe(document.body, { childList: true, subtree: true });
    // Full-page cart changes (cart type "page") and other tabs bypass fetch,
    // so check once on load and refresh if anything was out of sync.
    reconcile().then(function (changed) {
      if (!changed) return;
      if (window.theme && window.theme.pageType === 'cart' && window.theme.cartType === 'page') {
        window.location.reload();
      } else {
        refreshTheme();
      }
    });
  }
 
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
 
  window.cfmotoFees = { reconcile: reconcile, config: CONFIG };
})();